import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Papa from 'papaparse';

/**
 * Export data to CSV file
 */
export function exportToCSV(data: any[], filename: string) {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
}

/**
 * Export DOM element to PNG
 */
export async function exportToPNG(element: HTMLElement, filename: string) {
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
  });
  
  canvas.toBlob((blob) => {
    if (blob) {
      downloadBlob(blob, filename);
    }
  });
}

/**
 * Export page to PDF
 */
export async function exportPageToPDF(containerId: string, filename: string) {
  const element = document.getElementById(containerId);
  if (!element) {
    console.error(`Element with id "${containerId}" not found`);
    return;
  }

  // Capture the element as canvas
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
    logging: false,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
  const imgX = (pdfWidth - imgWidth * ratio) / 2;
  const imgY = 10;

  pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
  pdf.save(filename);
}

/**
 * Helper to trigger download of a Blob
 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Format number with commas
 */
export function formatNumber(num: number, decimals: number = 0): string {
  return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format currency
 */
export function formatCurrency(amount: number, currency: string = '£'): string {
  return `${currency}${formatNumber(amount, 2)}`;
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format trend indicator
 */
export function getTrendIndicator(trend: number | undefined): string {
  if (trend === undefined || trend === 0) return '';
  return trend > 0 ? '↑' : '↓';
}

/**
 * Get trend color class
 */
export function getTrendColorClass(trend: number | undefined, invertColors: boolean = false): string {
  if (trend === undefined || trend === 0) return 'text-gray-500';
  const isPositive = trend > 0;
  
  if (invertColors) {
    return isPositive ? 'text-red-500' : 'text-green-500';
  }
  
  return isPositive ? 'text-green-500' : 'text-red-500';
}

